import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '@/lib/logger';

export type PortalType = 'buyer' | 'supplier' | 'admin' | null;

interface PortalContextType {
  currentPortal: PortalType;
  setCurrentPortal: (portal: PortalType) => void;
  isBuyerPortal: boolean;
  isSupplierPortal: boolean;
  isAdminPortal: boolean;
}

const PortalContext = createContext<PortalContextType | undefined>(undefined);

function usePathnameSafe(): string {
  try {
    return useLocation().pathname;
  } catch {
    return typeof window !== 'undefined' ? window.location.pathname : '';
  }
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const pathname = usePathnameSafe();
  const [currentPortal, setCurrentPortal] = useState<PortalType>(null);

  // Detect portal from current route
  useEffect(() => {
    if (pathname.startsWith('/buyer/')) {
      setCurrentPortal('buyer');
      logger.debug('[PortalContext] Detected buyer portal', { pathname });
    } else if (pathname.startsWith('/supplier/')) {
      setCurrentPortal('supplier');
      logger.debug('[PortalContext] Detected supplier portal', { pathname });
    } else if (pathname.startsWith('/admin/')) {
      setCurrentPortal('admin');
      logger.debug('[PortalContext] Detected admin portal', { pathname });
    } else {
      // Public routes or other routes - no portal context
      setCurrentPortal(null);
      logger.debug('[PortalContext] No portal detected', { pathname });
    }
  }, [pathname]);

  const isBuyerPortal = currentPortal === 'buyer';
  const isSupplierPortal = currentPortal === 'supplier';
  const isAdminPortal = currentPortal === 'admin';

  return (
    <PortalContext.Provider
      value={{
        currentPortal,
        setCurrentPortal,
        isBuyerPortal,
        isSupplierPortal,
        isAdminPortal,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}

